import { Dialog } from '@headlessui/react';
import { X, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { formatPrice, buildApiUrl, calculateCartDiscounts, toCents, getStripeCurrency } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

function StripeCheckoutForm({
  total,
  storeInfo,
  isProcessing,
  setIsProcessing,
  customerEmail,
  onSuccessfulPayment,
}: {
  total: number;
  storeInfo: any;
  isProcessing: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
  customerEmail: string;
  onSuccessfulPayment: (paymentIntentId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error('Payment system not initialized');
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: customerEmail,
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        toast.success('Payment successful! Recording your order...');
        onSuccessfulPayment(paymentIntent.id);
      } else {
        toast.error('Payment processing. We will notify you upon completion.');
        setIsProcessing(false);
      }
    } catch (err: any) {
      toast.error(err?.message || 'An unexpected error occurred.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          defaultValues: {
            billingDetails: {
              email: customerEmail,
            },
          },
          fields: {
            billingDetails: 'never',
          },
        }}
      />
      <Button type="submit" disabled={!stripe || isProcessing} className="w-full mt-4">
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Processing...
          </div>
        ) : (
          `Pay ${formatPrice(total, storeInfo?.currenCy)}`
        )}
      </Button>
    </form>
  );
}

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (paymentMethod: string, paymentReference?: string) => void;
  orderTotal: number;
  rawSubtotal: number;
  shippingTotal: number;
  vatAmount: number;
  customerEmail: string;
  customerName: string;
  storeInfo: any;
  cart: any[];
  selectedCountry: string;
  selectedState: string;
  selectedLocation: any;
}

export function PaymentMethodDialog({
  open,
  onClose,
  onConfirm,
  orderTotal,
  rawSubtotal,
  shippingTotal,
  vatAmount,
  customerEmail,
  customerName,
  storeInfo,
  cart,
  selectedCountry,
  selectedState,
  selectedLocation,
}: PaymentMethodDialogProps) {
  const location = useLocation();
  const [clientSecret, setClientSecret] = useState<string>('');
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const total = orderTotal + shippingTotal + vatAmount;

  useEffect(() => {
    if (location.state?.paymentStatus === 'success') {
      setPaymentSuccess(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (open) {
      setIsStripeLoading(true);
      const controller = new AbortController();

      fetch(buildApiUrl('create-payment-intent.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          amount: toCents(total),
          currency: getStripeCurrency(storeInfo?.currenCy),
          email: customerEmail,
          domain: window.location.hostname,
          metadata: {
            customerName,
            customerEmail,
            country: selectedCountry,
            state: selectedState,
            shipping: selectedLocation?.name,
          },
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data && data.clientSecret && data.publishableKey) {
            setClientSecret(data.clientSecret);
            if (data.publishableKey !== publishableKey) {
              setPublishableKey(data.publishableKey);
              setStripePromise(loadStripe(data.publishableKey));
            }
          } else {
            throw new Error(data?.error || 'Invalid Stripe response');
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('Stripe init error', err);
            toast.error(err.message || 'Failed to initialize Stripe payment');
          }
        })
        .finally(() => setIsStripeLoading(false));

      return () => controller.abort();
    }

    setClientSecret('');
    setStripePromise(null);
    setPublishableKey(null);
  }, [open, total, customerEmail, customerName, selectedCountry, selectedState, selectedLocation, storeInfo?.currenCy]);

  if (!storeInfo) {
    return null;
  }

  const { totalDiscount } = calculateCartDiscounts(cart);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white dark:bg-gray-800">
          <div className="p-6 border-b dark:border-gray-700">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                {paymentSuccess ? 'Payment Successful' : 'Payment Method'}
              </Dialog.Title>
              {!paymentSuccess && !isProcessing && (
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[calc(100vh-16rem)] overflow-y-auto p-6 custom-scrollbar">
            {paymentSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Payment Successful!</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Your order is being processed...</p>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Order Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Items Total:</span>
                      <span className="font-medium">{formatPrice(rawSubtotal)}</span>
                    </div>

                    {totalDiscount > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-green-600 dark:text-green-400">Discounts:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">-{formatPrice(totalDiscount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-300">Subtotal:</span>
                          <span className="font-medium">{formatPrice(orderTotal)}</span>
                        </div>
                      </>
                    )}

                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Shipping:</span>
                      <span className="font-medium">{formatPrice(shippingTotal, storeInfo?.currenCy)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">VAT:</span>
                      <span className="font-medium">{formatPrice(vatAmount, storeInfo?.currenCy)}</span>
                    </div>
                    <div className="pt-2 border-t dark:border-gray-600">
                      <div className="flex justify-between font-medium">
                        <span className="text-gray-900 dark:text-white">Total:</span>
                        <span className="text-gray-900 dark:text-white">{formatPrice(total, storeInfo?.currenCy)}</span>
                      </div>
                      {totalDiscount > 0 && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">You saved {formatPrice(totalDiscount)}!</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 border rounded-lg dark:border-gray-700">
                    <label className="flex items-center">
                      <input type="radio" checked={true} className="mr-2" readOnly />
                      <span className="font-medium dark:text-white">Pay with Stripe</span>
                    </label>
                    {isStripeLoading ? (
                      <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
                    ) : stripePromise && clientSecret ? (
                      <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <StripeCheckoutForm
                          total={total}
                          storeInfo={storeInfo}
                          isProcessing={isProcessing}
                          setIsProcessing={setIsProcessing}
                          customerEmail={customerEmail}
                          onSuccessfulPayment={(paymentIntentId) => onConfirm('stripe', paymentIntentId)}
                        />
                      </Elements>
                    ) : (
                      <p className="text-sm text-red-600 dark:text-red-400">Failed to initialize payment. Please try again or contact support.</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
